import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OtpAuth from "@/pages/OtpAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import LocationInput, { haversineKm } from "@/components/LocationInput";
import RouteMap from "@/components/RouteMap";
import { playBell, startBellLoop, stopBellLoop } from "@/lib/sound";
import { vehicleInclusions, vehicleSeats, vehicleImage } from "@/lib/inclusions";
import { INDIAN_STATES } from "@/lib/states";
import {
  Truck, LogOut, Plus, X, ArrowRight, ArrowLeft, Clock, Loader2, Car, CheckCircle2, XCircle, MapPin, Inbox, Check, Users, Bell, Loader2 as Spin,
} from "lucide-react";

const TRIP_TYPES = ["One Way", "Round Trip", "Rental", "Hill Station", "Cab Package"];
const RENTAL_PACKAGES = ["4hr / 40km", "8hr / 80km", "12hr / 120km"];
const PAX_OPTIONS = [2, 3, 4, 5, 6, 12];
const inr = (n) => (n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN"));
const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
const legacyBand = (n) => (!n || n <= 2 ? "1-2" : n <= 4 ? "3-4" : "5+");
const photoUrl = (p) => `${API_ROOT}/itinerary-photo?path=${encodeURIComponent(p)}`;

export default function CustomerPortal() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Spin className="w-8 h-8 animate-spin text-amber-500" /></div>;
  return <CustomerApp />;
}

function CustomerApp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTrip = TRIP_TYPES.includes(searchParams.get("trip")) ? searchParams.get("trip") : "One Way";
  const prefill = useLocation().state?.prefill || {};
  const initialPax = searchParams.get("pax") || prefill.passengers || "";
  const { user, logout } = useAuth();
  const [vtypes, setVtypes] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState({});
  const [form, setForm] = useState({
    trip_type: initialTrip, from_location: prefill.fromLoc?.name || "", to_location: prefill.toLoc?.name || "",
    travel_date: prefill.travel_date || "", travel_time: prefill.travel_time || "",
    return_date: prefill.return_date || "", return_time: "", rental_package: prefill.rental_package || RENTAL_PACKAGES[1],
    cab_package: "", vehicle_type: prefill.vehicle_type || "", fare_offer: "", passengers: initialPax, night_halts: "",
    customer_phone: user?.phone || "", customer_name: "", state: "",
  });
  const [stops, setStops] = useState([]);
  const [fromLoc, setFromLoc] = useState(prefill.fromLoc || null);
  const [toLoc, setToLoc] = useState(prefill.toLoc || null);
  const [submitted, setSubmitted] = useState(null);
  const [hillRates, setHillRates] = useState([]);
  const [packages, setPackages] = useState([]);
  const [ringing, setRinging] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const acceptedRef = useRef(null);
  const soundRef = useRef(false);

  const loadEnquiries = async () => {
    if (!user) return;
    try {
      const r = await api.get("/customer/enquiries");
      const acceptedNow = r.data.filter((e) => e.accepted_by).map((e) => e.id);
      if (acceptedRef.current !== null) {
        const isNew = acceptedNow.some((id) => !acceptedRef.current.includes(id));
        if (isNew) { setRinging(true); if (soundRef.current) startBellLoop(); toast.success("A driver accepted your enquiry! 🎉"); }
      }
      acceptedRef.current = acceptedNow;
      setEnquiries(r.data);
    } catch (e) {}
  };
  useEffect(() => {
    api.get("/vehicle-types").then((r) => { setVtypes(r.data); setForm((f) => ({ ...f, vehicle_type: f.vehicle_type || r.data[0]?.name || "" })); }).catch(() => {});
    api.get("/hill-rates").then((r) => setHillRates(r.data)).catch(() => {});
    api.get("/itineraries?kind=cab_package").then((r) => setPackages(r.data)).catch(() => {});
    return () => { stopBellLoop(); };
  }, []);

  useEffect(() => {
    loadEnquiries();
    const t = setInterval(loadEnquiries, 7000);
    return () => { clearInterval(t); };
  }, [user]);

  const stopRing = () => { setRinging(false); stopBellLoop(); };
  const enableSound = () => { soundRef.current = true; playBell(); };

  useEffect(() => {
    if (form.trip_type === "Round Trip" && form.travel_date && form.return_date) {
      const days = Math.max(0, Math.round((new Date(form.return_date) - new Date(form.travel_date)) / 86400000));
      setForm((f) => ({ ...f, night_halts: String(Math.min(5, days)) }));
    }
  }, [form.trip_type, form.travel_date, form.return_date]);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const tt = form.trip_type;
  const needsTo = tt !== "Rental" && tt !== "Cab Package";
  const distKm = fromLoc && toLoc ? Math.round(haversineKm(fromLoc, toLoc) * 1.3) : null;
  const selVt = vtypes.find((v) => v.name === form.vehicle_type);
  const estKm = distKm && tt === "Round Trip" ? distKm * 2 : distKm;
  const nightHaltN = (tt === "One Way" || tt === "Round Trip") ? Number(form.night_halts || 0) : 0;
  const nightHaltCharge = nightHaltN > 0 && selVt?.night_halt_rate ? nightHaltN * selVt.night_halt_rate : 0;
  const rtHours = (tt === "Round Trip" && form.travel_date && form.return_date)
    ? Math.max(0, (new Date(`${form.return_date}T${form.return_time || "20:00"}`) - new Date(`${form.travel_date}T${form.travel_time || "08:00"}`)) / 36e5)
    : null;
  let estimate = null;
  let rtBasis = null;
  if (selVt) {
    if (tt === "Rental") {
      estimate = selVt.rental_rates?.[form.rental_package] ?? null;
    } else if (tt === "Round Trip") {
      const kmFare = distKm ? selVt.base_fare + distKm * 2 * (selVt.round_trip_per_km || selVt.per_km_rate) : null;
      const hrFare = rtHours != null && selVt.round_trip_per_hour ? selVt.base_fare + rtHours * selVt.round_trip_per_hour : null;
      if (kmFare != null && hrFare != null) { estimate = Math.round(Math.max(kmFare, hrFare)); rtBasis = kmFare >= hrFare ? "km" : "hours"; }
      else if (kmFare != null) { estimate = Math.round(kmFare); rtBasis = "km"; }
      else if (hrFare != null) { estimate = Math.round(hrFare); rtBasis = "hours"; }
    } else if (estKm) {
      estimate = Math.round(selVt.base_fare + estKm * selVt.per_km_rate);
    }
  }
  const totalEstimate = estimate != null ? estimate + nightHaltCharge : null;
  const matchAlias = (loc, names) => !!loc?.name && (names || []).some((n) => loc.name.toLowerCase().includes(n.trim().toLowerCase()));
  const hillMatch = tt === "Hill Station" && fromLoc && toLoc
    ? hillRates.find((r) => matchAlias(fromLoc, r.from_names) && matchAlias(toLoc, r.to_names))
    : null;
  const hillFare = hillMatch && form.vehicle_type ? hillMatch.rates?.[form.vehicle_type] ?? null : null;
  const selPackage = tt === "Cab Package" ? packages.find((p) => p.id === form.cab_package) : null;
  const paxRates = selPackage?.pax_rates?.[form.vehicle_type];
  const packageFare = selPackage && form.vehicle_type && form.passengers
    ? (paxRates?.[String(form.passengers)] ?? paxRates?.[legacyBand(Number(form.passengers))] ?? null)
    : null;

  const backToPackages = () => setForm((f) => ({ ...f, cab_package: "", passengers: "", fare_offer: "" }));

  const submit = async () => {
    if (tt === "Cab Package") {
      if (!form.cab_package) return toast.error("Select a package");
      if (!form.passengers) return toast.error("Select number of passengers");
    } else {
      if (!form.from_location) return toast.error("Enter pickup location");
      if (needsTo && !form.to_location) return toast.error("Enter destination");
    }
    if (!form.travel_date) return toast.error("Select travel date");
    if (!form.state) return toast.error("Select your State");
    if (!user && !form.customer_name.trim()) return toast.error("Enter your name");
    if (!form.customer_phone) return toast.error("Enter your phone number");
    const fare = form.fare_offer ? Number(form.fare_offer) : (packageFare ?? hillFare ?? totalEstimate);
    setSubmitting(true);
    try {
      const res = await api.post("/leads", {
        ...form,
        cab_package: selPackage ? selPackage.title : form.cab_package,
        from_location: form.from_location || (selPackage ? selPackage.title : ""),
        to_location: form.to_location || (selPackage ? (selPackage.destination || selPackage.title) : "") || form.from_location,
        customer_name: user ? user.name : form.customer_name.trim(),
        fare_offer: form.fare_offer ? Number(form.fare_offer) : (packageFare ?? null),
        quoted_fare: packageFare ?? hillFare ?? totalEstimate,
        passengers: form.passengers ? Number(form.passengers) : null,
        night_halts: Number(form.night_halts || 0) || null,
        stopovers: stops.filter(Boolean),
        from_lat: fromLoc?.lat ?? null,
        from_lng: fromLoc?.lng ?? null,
        to_lat: toLoc?.lat ?? null,
        to_lng: toLoc?.lng ?? null,
        distance_km: distKm,
      });
      if (!user) {
        const pending = JSON.parse(localStorage.getItem("rw_pending_claims") || "[]");
        localStorage.setItem("rw_pending_claims", JSON.stringify([...pending, { id: res.data.id, token: res.data.claim_token }]));
        setSubmitted({ id: res.data.id, fare, route: `${res.data.from_location} → ${res.data.to_location}`, vehicle: res.data.vehicle_type, trip: res.data.trip_type });
        toast.success("Enquiry sent to Admin! Please verify your number or email.");
      } else {
        toast.success("Enquiry submitted! Drivers will bid shortly.");
      }
      setStops([]);
      if (tt === "Cab Package") backToPackages();
      loadEnquiries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  const act = async (leadId, driverId, action) => {
    setBusy({ ...busy, [driverId + action]: true });
    try {
      await api.post(`/customer/offers/${leadId}/${driverId}/${action}`);
      toast.success(action === "accept" ? "Driver assigned!" : "Offer rejected");
      loadEnquiries();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy({ ...busy, [driverId + action]: false }); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  useEffect(() => {
    const pending = JSON.parse(localStorage.getItem("rw_pending_claims") || "[]");
    if (!user || user.role !== "customer" || !pending.length) return;
    setShowVerify(false);
    setSubmitted(null);
    Promise.allSettled(pending.map((p) => api.post(`/leads/${p.id}/claim`, { claim_token: p.token }))).then((results) => {
      const failed = pending.filter((_, i) => results[i].status === "rejected" && ![403, 404].includes(results[i].reason?.response?.status));
      localStorage.setItem("rw_pending_claims", JSON.stringify(failed));
      if (failed.length) toast.error("Could not confirm your enquiry. Please refresh to retry.");
      else toast.success("Your enquiry is confirmed and will reach drivers soon.");
      loadEnquiries();
    });
  }, [user]);

  const showListing = tt === "Cab Package" && !form.cab_package;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-portal">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-slate-200/80">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center"><Truck className="w-5 h-5 text-slate-900" /></div>
            <div><p className="font-display font-bold text-slate-900 leading-tight">Book a Cab</p><p className="text-xs text-slate-500 -mt-0.5">{user?.name || "Guest"}</p></div>
          </div>
          <div className="flex items-center gap-2">
            {user && (
              <button data-testid="customer-notification-bell" onClick={() => { enableSound(); stopRing(); }}
                title="Enable sound alerts" className="relative w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50">
                {ringing && <span data-testid="customer-ringing-indicator" className="absolute inset-0 rounded-full bg-amber-500/40 animate-ping" />}
                <Bell className={`w-5 h-5 text-amber-500 ${ringing ? "animate-bell" : ""}`} />
              </button>
            )}
            {user ? (
              <Button data-testid="customer-logout-button" variant="outline" onClick={doLogout} className="rounded-full border-slate-300"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
            ) : (
              <Button data-testid="customer-login-button" variant="outline" onClick={() => setShowVerify(true)} className="rounded-full border-amber-300 text-amber-700">Login</Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <Tabs defaultValue="new">
          <TabsList className="bg-white border border-slate-200 rounded-xl p-1">
            <TabsTrigger value="new" data-testid="customer-tab-new" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">New Enquiry</TabsTrigger>
            <TabsTrigger value="mine" data-testid="customer-tab-mine" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">My Enquiries</TabsTrigger>
          </TabsList>

          <TabsContent value="new" className="mt-5 space-y-4">
            {/* Trip type — always visible */}
            <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
              <Label className="text-sm">Trip Type</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {TRIP_TYPES.map((t) => (
                  <button key={t} data-testid={`trip-type-${t.replace(/\s/g, "-").toLowerCase()}`} onClick={() => set("trip_type", t)}
                    className={`px-3.5 py-2 rounded-full text-sm font-medium border ${tt === t ? "bg-amber-500 border-amber-500 text-slate-900" : "border-slate-200 text-slate-600 hover:border-amber-300"}`}>{t}</button>
                ))}
              </div>
            </div>

            {showListing ? (
              /* ---------- Cab Package listing ---------- */
              <div data-testid="package-listing">
                {packages.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No packages available yet — check back soon.</div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {packages.map((p) => (
                      <button key={p.id} data-testid={`package-card-${p.id}`} onClick={() => set("cab_package", p.id)}
                        className="text-left bg-white rounded-2xl border border-slate-200/80 shadow-[0_2px_8px_rgba(15,23,42,0.06)] overflow-hidden hover:border-amber-300 hover:shadow-md transition-all group">
                        <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                          {p.cover_photo_path ? (
                            <img src={photoUrl(p.cover_photo_path)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-300"><Car className="w-10 h-10" /></div>
                          )}
                        </div>
                        <div className="p-4">
                          <p className="font-display font-bold text-slate-900">{p.title}</p>
                          {p.destination && <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.destination}</p>}
                          <div className="mt-2 flex items-center justify-between">
                            <span className="text-xs text-slate-400">{p.days?.length || 0} days</span>
                            {p.price_from ? <span className="text-sm font-bold text-emerald-600">from {inr(p.price_from)}</span> : null}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* ---------- Booking form (package detail OR standard) ---------- */
              <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)] space-y-4">
                {tt === "Cab Package" ? (
                  <>
                    <button data-testid="package-back-button" onClick={backToPackages} className="text-sm font-medium text-amber-600 hover:underline flex items-center gap-1">
                      <ArrowLeft className="w-4 h-4" /> All packages
                    </button>

                    {selPackage && (
                      <div data-testid="package-detail" className="space-y-4">
                        {selPackage.cover_photo_path && (
                          <img src={photoUrl(selPackage.cover_photo_path)} alt={selPackage.title} data-testid="package-cover-photo" className="w-full aspect-[16/9] object-cover rounded-xl border border-slate-200" loading="lazy" />
                        )}
                        <div>
                          <p className="font-display text-xl font-extrabold text-slate-900">{selPackage.title}</p>
                          {selPackage.destination && <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" /> {selPackage.destination}</p>}
                          {selPackage.summary && <p className="text-sm text-slate-600 mt-1">{selPackage.summary}</p>}
                        </div>

                        {(selPackage.days || []).length > 0 && (
                          <div>
                            <p className="text-sm font-semibold text-slate-900 mb-2">Day-wise Itinerary</p>
                            <div className="space-y-3">
                              {selPackage.days.map((d) => (
                                <div key={d.day} className="flex gap-3">
                                  {d.photo_path ? (
                                    <img src={photoUrl(d.photo_path)} alt={`Day ${d.day}`} data-testid={`package-day-photo-${d.day}`} className="w-20 h-20 rounded-lg object-cover border border-slate-200 shrink-0" loading="lazy" />
                                  ) : (
                                    <span className="w-20 h-20 shrink-0 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold flex items-center justify-center">D{d.day}</span>
                                  )}
                                  <div><p className="font-medium text-slate-800 text-sm">Day {d.day}: {d.title}</p>{d.detail && <p className="text-xs text-slate-500 mt-0.5">{d.detail}</p>}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {(selPackage.inclusions?.length > 0 || selPackage.exclusions?.length > 0) && (
                          <div className="grid sm:grid-cols-2 gap-4">
                            {selPackage.inclusions?.length > 0 && (
                              <div data-testid="package-inclusions">
                                <p className="text-sm font-semibold text-emerald-700 mb-1.5">Inclusions</p>
                                <ul className="space-y-1">{selPackage.inclusions.map((x, i) => <li key={i} className="text-sm text-slate-600 flex gap-1.5"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{x}</li>)}</ul>
                              </div>
                            )}
                            {selPackage.exclusions?.length > 0 && (
                              <div data-testid="package-exclusions">
                                <p className="text-sm font-semibold text-red-600 mb-1.5">Exclusions</p>
                                <ul className="space-y-1">{selPackage.exclusions.map((x, i) => <li key={i} className="text-sm text-slate-600 flex gap-1.5"><X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />{x}</li>)}</ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t border-slate-100">
                      <Field label="Vehicle Type">
                        <Select value={form.vehicle_type} onValueChange={(v) => set("vehicle_type", v)}>
                          <SelectTrigger data-testid="package-vehicle-select"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{vtypes.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}{vehicleSeats(v.name) ? ` · ${vehicleSeats(v.name)} seats` : ""}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="No. of Pax">
                        <Select value={form.passengers ? String(form.passengers) : ""} onValueChange={(v) => set("passengers", v)}>
                          <SelectTrigger data-testid="package-pax-select"><SelectValue placeholder="Select passengers" /></SelectTrigger>
                          <SelectContent>{PAX_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n} passengers</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="Travel Date"><Input data-testid="enquiry-date-input" type="date" value={form.travel_date} onChange={(e) => set("travel_date", e.target.value)} /></Field>
                      <Field label="State">
                        <Select value={form.state} onValueChange={(v) => set("state", v)}>
                          <SelectTrigger data-testid="enquiry-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                          <SelectContent className="max-h-64">{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="Your Phone">
                        <div className="flex items-center rounded-md border border-input px-3"><span className="text-slate-500 text-sm mr-2">+91</span>
                          <Input data-testid="enquiry-phone-input" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="98765 43210" className="border-0 shadow-none focus-visible:ring-0 px-0" /></div>
                      </Field>
                      <Field label="Offer Your Fare (optional)"><Input data-testid="enquiry-offer-input" type="number" value={form.fare_offer} onChange={(e) => set("fare_offer", e.target.value)} placeholder="e.g. 18000" /></Field>
                    </div>

                    {packageFare != null && (
                      <div data-testid="package-fare" className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-emerald-700">Package Rate · {form.vehicle_type} · {form.passengers} pax</span>
                          <span className="font-display text-xl font-extrabold text-emerald-700">{inr(packageFare)}</span>
                        </div>
                        <p className="text-xs text-emerald-600/80 mt-0.5">Total fare · Toll & taxes included</p>
                      </div>
                    )}
                    {selPackage && form.vehicle_type && form.passengers && packageFare == null && (
                      <p data-testid="package-no-rate" className="text-xs text-amber-600">No preset rate for {form.vehicle_type} at {form.passengers} pax — submit and admin will price it.</p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Pickup Location">
                        <div className="rounded-md border border-input px-3 h-10 flex items-center">
                          <LocationInput testId="enquiry-from-input" placeholder="Pickup location (GPS)" value={fromLoc} allowLocate
                            onSelect={(loc) => { setFromLoc(loc); set("from_location", loc ? loc.name : ""); }} />
                        </div>
                      </Field>
                      {needsTo && (
                        <Field label={tt === "Hill Station" ? "Hill Station Destination" : "Destination"}>
                          <div className="rounded-md border border-input px-3 h-10 flex items-center">
                            <LocationInput testId="enquiry-to-input" placeholder={tt === "Hill Station" ? "e.g. Manali, Shimla (GPS)" : "Drop location (GPS)"} value={toLoc}
                              onSelect={(loc) => { setToLoc(loc); set("to_location", loc ? loc.name : ""); }} />
                          </div>
                        </Field>
                      )}
                      {tt === "Rental" && (
                        <Field label="Rental Package">
                          <Select value={form.rental_package} onValueChange={(v) => set("rental_package", v)}>
                            <SelectTrigger data-testid="enquiry-rental-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{RENTAL_PACKAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                          </Select>
                        </Field>
                      )}
                      <Field label="Travel Date"><Input data-testid="enquiry-date-input" type="date" value={form.travel_date} onChange={(e) => set("travel_date", e.target.value)} /></Field>
                      <Field label="Travel Time"><Input data-testid="enquiry-time-input" type="time" value={form.travel_time} onChange={(e) => set("travel_time", e.target.value)} /></Field>
                      {(tt === "Round Trip" || tt === "Hill Station") && (<>
                        <Field label="Return Date"><Input data-testid="enquiry-return-date-input" type="date" value={form.return_date} onChange={(e) => set("return_date", e.target.value)} /></Field>
                        <Field label="Return Time"><Input data-testid="enquiry-return-time-input" type="time" value={form.return_time} onChange={(e) => set("return_time", e.target.value)} /></Field>
                      </>)}
                      <Field label="State">
                        <Select value={form.state} onValueChange={(v) => set("state", v)}>
                          <SelectTrigger data-testid="enquiry-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                          <SelectContent className="max-h-64">{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="Vehicle Type">
                        <Select value={form.vehicle_type} onValueChange={(v) => set("vehicle_type", v)}>
                          <SelectTrigger data-testid="enquiry-vehicle-select"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{vtypes.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}{vehicleSeats(v.name) ? ` · ${vehicleSeats(v.name)} seats` : ""}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      {!user && (
                        <Field label="Your Name"><Input data-testid="enquiry-name-input" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Full name" /></Field>
                      )}
                      <Field label="Your Phone">
                        <div className="flex items-center rounded-md border border-input px-3"><span className="text-slate-500 text-sm mr-2">+91</span>
                          <Input data-testid="enquiry-phone-input" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="98765 43210" className="border-0 shadow-none focus-visible:ring-0 px-0" /></div>
                      </Field>
                      <Field label="Offer Your Fare (optional)"><Input data-testid="enquiry-offer-input" type="number" value={form.fare_offer} onChange={(e) => set("fare_offer", e.target.value)} placeholder="e.g. 3500" /></Field>
                      <Field label="No. of Pax"><Input data-testid="enquiry-pax-input" type="number" min="1" value={form.passengers} onChange={(e) => set("passengers", e.target.value)} placeholder="e.g. 4" /></Field>
                      {(tt === "One Way" || tt === "Round Trip") && (
                        <Field label="Night Halts (driver overnight)">
                          <Select value={form.night_halts || "0"} onValueChange={(v) => set("night_halts", v)}>
                            <SelectTrigger data-testid="enquiry-nighthalt-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{[0, 1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n === 0 ? "None" : `${n} night${n > 1 ? "s" : ""}`}</SelectItem>)}</SelectContent>
                          </Select>
                        </Field>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Stopovers (optional)</Label>
                        <Button data-testid="add-stopover-button" size="sm" variant="outline" onClick={() => setStops([...stops, ""])} className="h-8 rounded-lg"><Plus className="w-4 h-4 mr-1" /> Add stop</Button>
                      </div>
                      {stops.map((s, i) => (
                        <div key={i} className="mt-2 flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-amber-500" />
                          <div className="flex-1 rounded-md border border-input px-3 h-10 flex items-center">
                            <LocationInput testId={`stopover-input-${i}`} placeholder={`Stop ${i + 1} (GPS)`}
                              onSelect={(loc) => setStops(stops.map((x, j) => (j === i ? (loc ? loc.name : "") : x)))} />
                          </div>
                          <button onClick={() => setStops(stops.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>

                    {(hillFare != null || estimate != null) && (
                      <div data-testid="enquiry-fare-estimate" className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-emerald-700 flex items-center gap-2">
                            {vehicleImage(form.vehicle_type) && (
                              <img src={vehicleImage(form.vehicle_type)} alt={form.vehicle_type} data-testid="estimate-vehicle-photo" className="w-10 h-7 rounded-md object-cover border border-emerald-200" loading="lazy" />
                            )}
                            {hillFare != null ? "Admin Fixed Rate" : tt === "Rental" ? "Rental Package Rate" : `Estimated fare${tt === "Round Trip" ? " (round trip)" : ""}`}
                          </span>
                          <span className="font-display text-xl font-extrabold text-emerald-700">{inr(hillFare ?? totalEstimate)}</span>
                        </div>
                        <p className="text-xs text-emerald-600/80 mt-0.5">
                          {hillFare != null
                            ? `${hillMatch.from_names[0]} → ${hillMatch.to_names[0]} · ${form.vehicle_type} · Toll & taxes included`
                            : tt === "Rental"
                            ? `${form.rental_package} · ${form.vehicle_type} · Fuel & driver included`
                            : tt === "Round Trip"
                            ? `Base ${inr(selVt.base_fare)} + ${rtBasis === "hours" ? `~${Math.round(rtHours)} hrs × ${inr(selVt.round_trip_per_hour)}/hr` : `~${estKm} km × ${inr(selVt.round_trip_per_km || selVt.per_km_rate)}/km`} (whichever is higher)${nightHaltCharge ? ` + Night halt ${nightHaltN} × ${inr(selVt.night_halt_rate)}` : ""}`
                            : `Base ${inr(selVt.base_fare)} + ~${estKm} km × ${inr(selVt.per_km_rate)}/km${nightHaltCharge ? ` + Night halt ${nightHaltN} × ${inr(selVt.night_halt_rate)}` : ""} · Toll & taxes included`}
                        </p>
                        <p data-testid="fare-inclusive-note" className="text-[11px] text-emerald-600/70 mt-1">
                          Price inclusive of vehicle, driver & toll taxes{nightHaltCharge ? " · night halt charges included" : ""}. Customer to provide fooding & lodging for the driver as per the tour.
                        </p>
                        <div data-testid="fare-inclusions" className="mt-2 flex flex-wrap gap-1.5">
                          {vehicleSeats(form.vehicle_type) && (
                            <span data-testid="inclusion-seats" className="inline-flex items-center gap-1 rounded-full bg-white border border-emerald-200 px-2 py-0.5 text-xs font-medium text-emerald-700">
                              <Users className="w-3 h-3" /> {vehicleSeats(form.vehicle_type)} seats
                            </span>
                          )}
                          {vehicleInclusions(form.vehicle_type).map((inc) => (
                            <span key={inc} data-testid={`inclusion-${inc.replace(/\s/g, "-").toLowerCase()}`}
                              className="inline-flex items-center gap-1 rounded-full bg-white border border-emerald-200 px-2 py-0.5 text-xs font-medium text-emerald-700">
                              <Check className="w-3 h-3" /> {inc}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <Button data-testid="enquiry-submit-button" onClick={submit} disabled={submitting} className="w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Enquiry"}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="mine" className="mt-5 space-y-3">
            {enquiries.length === 0 && (
              !user ? (
                <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
                  <Inbox className="w-10 h-10 text-slate-300 mx-auto" />
                  <p className="mt-3 text-slate-500 font-medium">Login to see your enquiries</p>
                  <Button data-testid="mine-login-button" onClick={() => setShowVerify(true)} className="mt-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">Login / Verify</Button>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
                  <Inbox className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500 font-medium">No enquiries yet</p>
                </div>
              )
            )}
            {enquiries.map((l) => (
              <div key={l.id} data-testid="customer-enquiry-card" className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-1.5 font-display font-bold text-slate-900">{l.from_location} <ArrowRight className="w-4 h-4 text-amber-500" /> {l.to_location}</div>
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-1"><Clock className="w-3 h-3" /> {formatDateTime(l.travel_date, l.travel_time)} · {l.trip_type} · {l.vehicle_type}</p>
                  </div>
                  <Badge className="bg-slate-100 text-slate-700 border border-slate-200 capitalize">{l.status}</Badge>
                </div>
                <div className="mt-3">
                  <RouteMap from={l.from_location} to={l.to_location} stops={l.stopovers} testId="customer-route-map" />
                </div>
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1.5">Driver Offers</p>
                  {(l.offers || []).length === 0 ? (
                    <p className="text-sm text-slate-400">No offers yet — waiting for drivers to bid.</p>
                  ) : (
                    <div className="space-y-2">
                      {l.offers.map((o) => (
                        <div key={o.driver_id} data-testid="customer-offer-row" className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2">
                          <span className="text-sm text-slate-700">{o.driver_name}: <b>{inr(o.amount)}</b></span>
                          {o.status === "pending" && !l.accepted_by ? (
                            <div className="flex gap-2">
                              <Button data-testid="customer-accept-offer" size="sm" onClick={() => act(l.id, o.driver_id, "accept")} disabled={busy[o.driver_id + "accept"]} className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-4 h-4 mr-1" /> Accept</Button>
                              <Button data-testid="customer-reject-offer" size="sm" variant="outline" onClick={() => act(l.id, o.driver_id, "reject")} disabled={busy[o.driver_id + "reject"]} className="h-8 rounded-lg border-red-200 text-red-600 hover:bg-red-50"><XCircle className="w-4 h-4" /></Button>
                            </div>
                          ) : (
                            <span className={`text-xs font-semibold capitalize ${o.status === "accepted" ? "text-emerald-600" : "text-red-500"}`}>{o.status}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </main>

      {submitted && !user && !showVerify && (
        <div data-testid="guest-submitted-overlay" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-xl">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center"><Check className="w-6 h-6 text-emerald-600" /></div>
            <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Enquiry sent to Admin</h2>
            <p className="mt-1 text-sm text-slate-500" data-testid="guest-submitted-route">{submitted.trip} · {submitted.route} · {submitted.vehicle}</p>
            <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 flex items-center justify-between">
              <span className="text-sm font-medium text-emerald-700">Your fare</span>
              <span data-testid="guest-submitted-fare" className="font-display text-xl font-extrabold text-emerald-700">{submitted.fare != null ? inr(submitted.fare) : "Admin will price it"}</span>
            </div>
            <p className="mt-4 text-sm text-slate-600">Verify your phone or email so drivers can receive your enquiry and you can track offers.</p>
            <Button data-testid="guest-verify-now-button" onClick={() => setShowVerify(true)} className="mt-4 w-full h-12 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">Verify now</Button>
            <button data-testid="guest-verify-later-button" onClick={() => setSubmitted(null)} className="mt-2 w-full text-sm text-slate-500 hover:text-slate-700">Later</button>
          </div>
        </div>
      )}

      {showVerify && !user && (
        <div data-testid="customer-verify-overlay" className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/80 backdrop-blur-sm">
          <button data-testid="customer-verify-close-button" onClick={() => setShowVerify(false)} aria-label="Close"
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
          <OtpAuth
            role="customer"
            title={submitted || localStorage.getItem("rw_pending_claims")?.length > 2 ? "Verify to confirm your enquiry" : "Login / Verify"}
            subtitle={submitted || localStorage.getItem("rw_pending_claims")?.length > 2 ? "Your enquiry has reached Admin — verify so drivers can receive it." : "Enter the code we send you."}
            prefillChannel={form.customer_phone ? "sms" : "email"}
            prefillIdentifier={form.customer_phone || ""}
          />
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (<div><Label className="text-sm text-slate-600">{label}</Label><div className="mt-1.5">{children}</div></div>);
}
